import { useEffect, useState } from 'react';
import { Navigate, Route, useLocation, useNavigate } from 'react-router-dom';
import {
  REGISTRATION_ENABLED,
  REGISTRATION_MODE,
  SMS_AUTH_ENABLED,
} from '@/core/release-capabilities';
import {
  readRegistrationInvitation,
  removeRegistrationInvitation,
  removeRegistrationInvitationQuery,
} from '@/features/auth/helpers/registration-invitation';
import { GuestRoute, OnboardingGuard, ProtectedRoute } from '@/navigation/guards/route-guards';
import {
  parseLoginRecoveryReason,
  safeAuthReturnTarget,
} from '@/navigation/safe-return-target';
import { lazyNamed } from '../lazy-named';
import { ROUTE_PATHS } from '../constants';

const loadAuth = () => import('@/features/auth');
const ForgotPasswordPage = lazyNamed(loadAuth, 'ForgotPasswordPage');
const LoginPage = lazyNamed(loadAuth, 'LoginPage');
const RegisterPage = lazyNamed(loadAuth, 'RegisterPage');
const RegistrationInvitationRequiredPage = lazyNamed(
  loadAuth,
  'RegistrationInvitationRequiredPage',
);
const ResetPasswordOtpPage = lazyNamed(loadAuth, 'ResetPasswordOtpPage');
const ResetPasswordPage = lazyNamed(loadAuth, 'ResetPasswordPage');
const VerifyEmailPage = lazyNamed(loadAuth, 'VerifyEmailPage');
const VerifyPhonePage = lazyNamed(loadAuth, 'VerifyPhonePage');
const VerificationUnavailablePage = lazyNamed(
  () => import('@/features/auth/ui/pages/verification-unavailable-page'),
  'VerificationUnavailablePage',
);
const OnboardingPage = lazyNamed(
  () => import('@/features/onboarding/ui/pages/onboarding-page'),
  'OnboardingPage',
);

export function RegistrationEntry() {
  const location = useLocation();
  const navigate = useNavigate();
  const [invitationToken] = useState(() =>
    readRegistrationInvitation(location.hash),
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''));
    if (!searchParams.has('invite') && !hashParams.has('invite')) return;

    navigate(
      {
        pathname: location.pathname,
        search: removeRegistrationInvitationQuery(location.search),
        hash: removeRegistrationInvitation(location.hash),
      },
      { replace: true },
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  if (!REGISTRATION_ENABLED) {
    return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
  }

  if (REGISTRATION_MODE === 'invite_only' && !invitationToken) {
    return <RegistrationInvitationRequiredPage />;
  }

  return <RegisterPage invitationToken={invitationToken ?? undefined} />;
}

function LoginEntry() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  return (
    <LoginPage
      redirectTo={safeAuthReturnTarget(searchParams.get('returnTo')) ?? undefined}
      recoveryReason={parseLoginRecoveryReason(searchParams.get('reason')) ?? undefined}
    />
  );
}

export function authRoutes() {
  return (
    <>
      {/*
        Guest routes - auth pages mount AuthShell internally. Business-register
        remains a protected compatibility redirect into onboarding.
      */}
      <Route
        path={ROUTE_PATHS.LOGIN}
        element={
          <GuestRoute>
            <LoginEntry />
          </GuestRoute>
        }
      />
      <Route
        path={ROUTE_PATHS.REGISTER}
        element={
          <GuestRoute>
            <RegistrationEntry />
          </GuestRoute>
        }
      />
      <Route
        path={ROUTE_PATHS.ONBOARDING}
        element={
          <ProtectedRoute>
            <OnboardingGuard>
              <OnboardingPage />
            </OnboardingGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTE_PATHS.BUSINESS_REGISTER}
        element={
          <ProtectedRoute>
            <Navigate to={ROUTE_PATHS.ONBOARDING} replace />
          </ProtectedRoute>
        }
      />
      <Route path={ROUTE_PATHS.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
      <Route path={ROUTE_PATHS.RESET_PASSWORD} element={<ResetPasswordPage />} />
      <Route
        path={ROUTE_PATHS.RESET_PASSWORD_OTP}
        element={SMS_AUTH_ENABLED
          ? <ResetPasswordOtpPage />
          : <Navigate to={ROUTE_PATHS.FORGOT_PASSWORD} replace />}
      />
      <Route
        path={ROUTE_PATHS.VERIFY_EMAIL}
        element={
          <ProtectedRoute>
            <VerifyEmailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTE_PATHS.VERIFY_PHONE}
        element={
          <ProtectedRoute>
            {SMS_AUTH_ENABLED
              ? <VerifyPhonePage />
              : <VerificationUnavailablePage />}
          </ProtectedRoute>
        }
      />
    </>
  );
}
